/**
 * 6. 키워드 추천 / 연관검색어 / 자동완성 키워드 테이블 섹션
 * - 3개 카드를 Row 안에 Col span=8 (3열)로 배치
 *
 * [버그 수정] 기존 Row 안에 Col span=12가 3개 (총 36, 오버플로우) → span=8로 수정하여 3열 정렬
 *
 * @param {object} report - 분석 리포트 데이터
 *   - report.keywords          : 키워드 추천 목록
 *   - report.relatedQueries    : 연관검색어 목록
 *   - report.autocompleteKeywords : 자동완성 키워드 목록
 */
import React from 'react';
import { Row, Col, Card, Table, Space, Tooltip } from 'antd';
import {
    FileTextOutlined,
    LinkOutlined,
    SearchOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';
import { MOCK_KEYWORDS } from '../mockData';

const KeywordTablesSection = ({ report }) => {
    // 🔍 검색량이 적음/미만 기호가 포함된 경우에 대한 정밀 포맷터
    const renderSearchVolume = val => {
        if (val === null) {
            return (
                <span style={{ color: '#94a3b8', fontSize: 13, fontWeight: 500 }}>조회 중...</span>
            );
        }

        // 숫자 타입 처리 (10 또는 20 이하는 '적음'으로 표시)
        if (typeof val === 'number') {
            if (val <= 20) {
                return (
                    <span style={{ color: '#94a3b8', fontSize: 13, fontWeight: 500 }}>적음</span>
                );
            }
            return (
                <span style={{ color: '#3b82f6', fontWeight: 800, fontSize: 14 }}>
                    {val.toLocaleString()}
                </span>
            );
        }

        // 문자열 타입 처리 ('<' 기호 혹은 '적음' 단어 필터)
        if (typeof val === 'string') {
            const cleaned = val.replace(/\s+/g, '');
            if (cleaned.includes('<') || cleaned === '적음') {
                return (
                    <span style={{ color: '#94a3b8', fontSize: 13, fontWeight: 500 }}>적음</span>
                );
            }
            return <span style={{ color: '#3b82f6', fontWeight: 800, fontSize: 14 }}>{val}</span>;
        }

        return <span style={{ color: '#94a3b8', fontSize: 13, fontWeight: 500 }}>적음</span>;
    };

    // 🚀 검색량 기준 실시간 내림차순 정렬 유틸리티 (null/조회중은 가장 아래로 내림)
    const getSortedList = list => {
        if (!Array.isArray(list)) return [];
        return [...list].sort((a, b) => {
            const valA = a.search === null ? -1 : typeof a.search === 'number' ? a.search : 10;
            const valB = b.search === null ? -1 : typeof b.search === 'number' ? b.search : 10;
            return valB - valA;
        });
    };

    const sortedKeywords = getSortedList(report.keywords || MOCK_KEYWORDS);
    const sortedRelated = getSortedList(report.relatedQueries || []);
    const sortedAutocomplete = getSortedList(report.autocompleteKeywords || []);

    return (
        <Row gutter={16} style={{ marginTop: 24 }}>
            {/* 키워드 추천 */}
            <Col span={8}>
                <Card
                    title={
                        <span
                            style={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: '#0f172a',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 5,
                            }}
                        >
                            <FileTextOutlined style={{ color: '#3b82f6' }} /> 키워드 추천{' '}
                            <Tooltip title="네이버 쇼핑 기준 300위 이내 순위를 조회합니다.">
                                <InfoCircleOutlined style={{ fontSize: 12, color: '#94a3b8' }} />
                            </Tooltip>
                        </span>
                    }
                    bordered={false}
                    className="ant-table-wrapper"
                    bodyStyle={{ padding: 0 }}
                    headStyle={{ borderBottom: '3px solid #3b82f6' }}
                >
                    <Table
                        dataSource={sortedKeywords}
                        columns={[
                            {
                                title: '키워드명',
                                dataIndex: 'word',
                                key: 'word',
                                render: (text, record, idx) => {
                                    const displayText =
                                        typeof text === 'object'
                                            ? text.query || text.text || JSON.stringify(text)
                                            : text;
                                    return (
                                        <Space>
                                            <span style={{ fontSize: 13, color: '#94a3b8' }}>
                                                {idx + 1}
                                            </span>{' '}
                                            <span style={{ fontSize: 14 }}>{displayText}</span>
                                        </Space>
                                    );
                                },
                            },
                            {
                                title: '검색량',
                                dataIndex: 'search',
                                key: 'search',
                                render: renderSearchVolume,
                            },
                            {
                                title: '순위',
                                dataIndex: 'rank',
                                key: 'rank',
                                render: val =>
                                    val === null ? (
                                        <span
                                            style={{
                                                color: '#94a3b8',
                                                fontSize: 13,
                                                fontWeight: 500,
                                            }}
                                        >
                                            조회 중...
                                        </span>
                                    ) : val > 0 ? (
                                        <span
                                            style={{
                                                color: '#4f46e5',
                                                fontWeight: 800,
                                                fontSize: 14,
                                            }}
                                        >
                                            {val}위
                                        </span>
                                    ) : (
                                        <span
                                            style={{
                                                color: '#cbd5e1',
                                                fontWeight: 500,
                                                fontSize: 13,
                                            }}
                                        >
                                            미진입
                                        </span>
                                    ),
                            },
                        ]}
                        pagination={false}
                        size="middle"
                    />
                </Card>
            </Col>

            {/* 연관검색어 */}
            <Col span={8}>
                <Card
                    title={
                        <span
                            style={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: '#0f172a',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 5,
                            }}
                        >
                            <LinkOutlined style={{ color: '#10b981' }} /> 연관검색어{' '}
                            <Tooltip title="네이버 쇼핑 기준 300위 이내 순위를 조회합니다.">
                                <InfoCircleOutlined style={{ fontSize: 12, color: '#94a3b8' }} />
                            </Tooltip>
                        </span>
                    }
                    bordered={false}
                    className="ant-table-wrapper"
                    bodyStyle={{ padding: 0 }}
                    headStyle={{ borderBottom: '3px solid #10b981' }}
                >
                    <Table
                        dataSource={sortedRelated}
                        columns={[
                            {
                                title: '키워드',
                                dataIndex: 'word',
                                key: 'word',
                                render: (text, record, idx) => (
                                    <Space>
                                        <span
                                            style={{ color: '#10b981', fontSize: 13, minWidth: 16 }}
                                        >
                                            {idx + 1}
                                        </span>{' '}
                                        <span style={{ fontSize: 14 }}>{text}</span>
                                    </Space>
                                ),
                            },
                            {
                                title: '검색량',
                                dataIndex: 'search',
                                key: 'search',
                                render: renderSearchVolume,
                            },
                            {
                                title: '순위',
                                dataIndex: 'rank',
                                key: 'rank',
                                render: val =>
                                    val === null ? (
                                        <span
                                            style={{
                                                color: '#94a3b8',
                                                fontSize: 13,
                                                fontWeight: 500,
                                            }}
                                        >
                                            조회 중...
                                        </span>
                                    ) : val > 0 ? (
                                        <span
                                            style={{
                                                color: '#4f46e5',
                                                fontWeight: 800,
                                                fontSize: 14,
                                            }}
                                        >
                                            {val}위
                                        </span>
                                    ) : (
                                        <span
                                            style={{
                                                color: '#cbd5e1',
                                                fontWeight: 500,
                                                fontSize: 13,
                                            }}
                                        >
                                            미진입
                                        </span>
                                    ),
                            },
                        ]}
                        pagination={false}
                        size="middle"
                    />
                </Card>
            </Col>

            {/* 자동완성 키워드 */}
            <Col span={8}>
                <Card
                    title={
                        <span
                            style={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: '#0f172a',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 5,
                            }}
                        >
                            <SearchOutlined style={{ color: '#3b82f6' }} /> 자동완성 키워드{' '}
                            <Tooltip title="네이버 쇼핑 기준 300위 이내 순위를 조회합니다.">
                                <InfoCircleOutlined style={{ fontSize: 12, color: '#94a3b8' }} />
                            </Tooltip>
                        </span>
                    }
                    bordered={false}
                    className="ant-table-wrapper"
                    bodyStyle={{ padding: 0 }}
                    headStyle={{ borderBottom: '3px solid #3b82f6' }}
                >
                    <Table
                        dataSource={sortedAutocomplete}
                        columns={[
                            {
                                title: '키워드',
                                dataIndex: 'word',
                                key: 'word',
                                render: (text, record, idx) => (
                                    <Space>
                                        <span
                                            style={{ color: '#3b82f6', fontSize: 13, minWidth: 16 }}
                                        >
                                            {idx + 1}
                                        </span>{' '}
                                        <span style={{ fontSize: 14 }}>{text}</span>
                                    </Space>
                                ),
                            },
                            {
                                title: '검색량',
                                dataIndex: 'search',
                                key: 'search',
                                render: renderSearchVolume,
                            },
                            {
                                title: '순위',
                                dataIndex: 'rank',
                                key: 'rank',
                                render: val =>
                                    val === null ? (
                                        <span
                                            style={{
                                                color: '#94a3b8',
                                                fontSize: 13,
                                                fontWeight: 500,
                                            }}
                                        >
                                            조회 중...
                                        </span>
                                    ) : val > 0 ? (
                                        <span
                                            style={{
                                                color: '#4f46e5',
                                                fontWeight: 800,
                                                fontSize: 14,
                                            }}
                                        >
                                            {val}위
                                        </span>
                                    ) : (
                                        <span
                                            style={{
                                                color: '#cbd5e1',
                                                fontWeight: 500,
                                                fontSize: 13,
                                            }}
                                        >
                                            미진입
                                        </span>
                                    ),
                            },
                        ]}
                        pagination={false}
                        size="middle"
                    />
                </Card>
            </Col>
        </Row>
    );
};

export default KeywordTablesSection;
